import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';

import Blocks from '../views/Blocks';
import ColumnWrapper from './ColumnWrapper';
import WorksheetTitle from './WorksheetTitle';
import { EXPLORE_PATHS, WORKBOOK_META_FIELDS } from '../constants';
import { postDocumentScrollHeightMessage } from '../helpers/parent-messaging';
import { TimelineSwitch } from './TimelineSwitch';

const Worksheet = React.memo(({ template, path, setPath, ...etc }) => {
  React.useEffect(() => {
    window.parent.postMessage('SLED.performance.renderWorksheet', '*');
    postDocumentScrollHeightMessage();
  }, []);
  React.useEffect(() => {
    // This is useful for debugging - you can easily submit ops on live workbooks via doc.submitOp
    if (etc.currentUser?.isInstructor) {
      window.doc = etc.doc;
    }
  }, [etc.doc]);
  React.useEffect(() => {
    // The first time the worksheet is set to readOnly mode while open, make a copy of relevant
    //  data for the student and teacher explore versions.
    if (
      etc.readOnlyWorksheet &&
      !etc.doc.data[EXPLORE_PATHS.student] &&
      !etc.currentUser?.timeline
    ) {
      const dataToCopy = Object.keys(etc.doc.data)
        .filter((key) => !WORKBOOK_META_FIELDS.includes(key))
        .reduce((acc, x) => {
          acc[x] = etc.doc.data[x];
          return acc;
        }, {});
      [EXPLORE_PATHS.student, EXPLORE_PATHS.instructor].forEach((path) =>
        etc.doc.submitOp({
          p: [path],
          oi: dataToCopy,
        })
      );
    }
  }, [etc.doc, etc.readOnlyWorksheet]);
  return (
    <>
      <Helmet>
        <title>{template.title}</title>
      </Helmet>
      <ColumnWrapper className="worksheet-column-wrapper">
        <div className="flex justify-between items-center">
          <WorksheetTitle title={template.title} />
          <TimelineSwitch
            active={etc.currentUser?.timeline}
            open={etc.currentUser?.isGrading && etc.currentUser?.isAdmin}
          />
        </div>
        {etc.doc.data.readOnly && (
          <>
            <div className="p3">
              <a
                href="#"
                onClick={() => setPath(undefined)}
                style={{ paddingRight: '16px' }}
                className={
                  path === undefined
                    ? 'explore-tab-selected'
                    : 'explore-tab-unselected'
                }
              >
                Original Submission
              </a>
              {etc.currentUser.isInstructor ? (
                <a
                  href="#"
                  onClick={() => setPath(EXPLORE_PATHS.instructor)}
                  className={
                    path === EXPLORE_PATHS.instructor
                      ? 'explore-tab-selected'
                      : 'explore-tab-unselected'
                  }
                >
                  Explore
                </a>
              ) : (
                <a
                  href="#"
                  onClick={() => {
                    setPath(EXPLORE_PATHS.student);
                  }}
                  className={
                    path === EXPLORE_PATHS.student
                      ? 'explore-tab-selected'
                      : 'explore-tab-unselected'
                  }
                >
                  Explore
                </a>
              )}
            </div>
            {/*  Once the @minervaproject/fds library is integrated into this project, the below div should be */}
            {/*  converted to a <Alert kind="info"> component. */}
            <div
              className="m3 p3 bg-blue-tint-90 secondary-font border"
              style={{ borderColor: '#0A78BF' }}
            >
              {path ? (
                <span>
                  <b>
                    This tab can be used to further explore editing the original
                    submission.
                  </b>{' '}
                  <br />
                  Changes made on this tab will not affect the original
                  submission and will not be visible to{' '}
                  {path === EXPLORE_PATHS.student ? 'instructors' : 'students'}.
                </span>
              ) : (
                <span>
                  <b>
                    This workbook has been submitted and your submission cannot
                    be changed.
                  </b>{' '}
                  You can explore editing this workbook in the 'Explore' tab
                  above.
                </span>
              )}
            </div>
          </>
        )}
        {!path || etc.doc.data[path] ? (
          <Blocks
            key={path}
            generalPath={path}
            blocks={template.blocks}
            {...etc}
            readOnlyWorksheet={!path && etc.readOnlyWorksheet}
          />
        ) : null}
      </ColumnWrapper>
    </>
  );
});

Worksheet.propTypes = {
  template: PropTypes.object.isRequired,
};

export default Worksheet;
